import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, FileText, ArrowRight, ChevronLeft, Loader2, Globe, MapPin, ChevronDown, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '@/services/apiService';
import { useApp } from '@/context/AppContext';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';

// Available migration routes (MVP: USA → Spain only)
const MIGRATION_ROUTES = [
  { 
    id: 'usa-spain', 
    from: 'United States', 
    fromCode: 'US',
    to: 'Spain', 
    toCode: 'ES',
    available: true,
    flag: '🇺🇸 → 🇪🇸'
  },
  { 
    id: 'uk-spain', 
    from: 'United Kingdom', 
    fromCode: 'UK',
    to: 'Spain', 
    toCode: 'ES',
    available: false,
    flag: '🇬🇧 → 🇪🇸'
  },
  { 
    id: 'canada-spain', 
    from: 'Canada', 
    fromCode: 'CA',
    to: 'Spain', 
    toCode: 'ES',
    available: false,
    flag: '🇨🇦 → 🇪🇸'
  },
  { 
    id: 'usa-portugal', 
    from: 'United States', 
    fromCode: 'US',
    to: 'Portugal', 
    toCode: 'PT',
    available: false,
    flag: '🇺🇸 → 🇵🇹'
  },
];

const CountrySelection = () => {
  const navigate = useNavigate();
  const { startNewSession } = useApp();
  const { trackEvent } = useAnalytics();
  const [visaTypes, setVisaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(MIGRATION_ROUTES[0]);
  const [showRouteDropdown, setShowRouteDropdown] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyRoute, setNotifyRoute] = useState(null);

  useEffect(() => {
    // Start a fresh session when user selects visa type
    startNewSession();
    loadVisaTypes();
  }, [startNewSession]);

  const loadVisaTypes = async () => {
    try {
      const types = await apiService.getVisaTypes();
      setVisaTypes(types);
    } catch (error) {
      console.error('Failed to load visa types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVisaTypeClick = (visaType) => {
    // Track visa selection
    trackEvent(ANALYTICS_EVENTS.VISA_SELECTION, { 
      visa_type: visaType,
      migration_route: `${selectedRoute.from} → ${selectedRoute.to}`
    });
    // Navigate directly to documents page when visa type is clicked
    navigate('/documents', { state: { visaType } });
  };

  const handleRouteSelect = (route) => {
    if (route.available) {
      setSelectedRoute(route);
      setShowRouteDropdown(false);
    } else {
      // Show notify modal for unavailable routes
      setNotifyRoute(route);
      setShowNotifyModal(true);
      setShowRouteDropdown(false);
    }
  };

  const handleNotifySubmit = (e) => {
    e.preventDefault();
    // In a real implementation, this would save to database
    console.log(`Notify ${notifyEmail} when ${notifyRoute?.from} → ${notifyRoute?.to} is available`);
    trackEvent('notify_request', {
      email: notifyEmail,
      route: `${notifyRoute?.from} → ${notifyRoute?.to}`
    });
    setShowNotifyModal(false);
    setNotifyEmail('');
    setNotifyRoute(null);
    // Show success message
    alert(`Thanks! We'll notify you when ${notifyRoute?.from} → ${notifyRoute?.to} becomes available.`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900 mb-4 text-center">
            Where Are You Moving?
          </h1>
          <p className="font-public text-lg text-slate-600 mb-8 text-center">
            Select your migration route to see the required documents
          </p>

          {/* Migration Route Selector */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-trust" />
              <label className="font-public font-semibold text-slate-900">Migration Route</label>
            </div>
            
            {/* Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowRouteDropdown(!showRouteDropdown)}
                className="w-full p-4 rounded-lg border-2 border-slate-200 hover:border-trust bg-white flex items-center justify-between transition-colors"
                data-testid="route-selector"
              >
                <div className="flex items-center gap-4">
                  <span className="text-2xl">{selectedRoute.flag}</span>
                  <div className="text-left">
                    <div className="font-public font-semibold text-slate-900">
                      {selectedRoute.from} → {selectedRoute.to}
                    </div>
                    <div className="text-sm text-slate-500">
                      Currently available
                    </div>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showRouteDropdown ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showRouteDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg z-20 overflow-hidden"
                  >
                    {MIGRATION_ROUTES.map((route) => (
                      <button
                        key={route.id}
                        onClick={() => handleRouteSelect(route)}
                        className={`w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 ${
                          !route.available ? 'opacity-60' : ''
                        } ${selectedRoute.id === route.id ? 'bg-trust/5' : ''}`}
                        data-testid={`route-option-${route.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-2xl">{route.flag}</span>
                          <div className="text-left">
                            <div className="font-public font-semibold text-slate-900">
                              {route.from} → {route.to}
                            </div>
                            <div className="text-sm text-slate-500">
                              {route.available ? 'Available now' : 'Coming soon'}
                            </div>
                          </div>
                        </div>
                        {!route.available && (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                            Notify me
                          </span>
                        )}
                        {selectedRoute.id === route.id && route.available && (
                          <span className="text-trust">✓</span>
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Coming Soon Notice */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
              <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-900 font-medium">More destinations coming soon!</p>
                <p className="text-xs text-blue-700 mt-1">
                  We're starting with USA → Spain and will be adding more migration routes including UK, Canada, Portugal, and more.
                </p>
              </div>
            </div>
          </div>

          {/* Visa Type Selection */}
          <h2 className="font-outfit font-bold text-2xl text-slate-900 mb-2 text-center">
            Select Your Visa Type
          </h2>
          <p className="font-public text-slate-600 mb-6 text-center">
            for {selectedRoute.to} visa requirements
          </p>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 text-trust animate-spin mx-auto" />
              <p className="mt-4 text-slate-600">Loading visa types...</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <FileText className="w-5 h-5 text-trust" />
                <label className="font-public font-semibold text-slate-900">Choose a Visa Type</label>
              </div>
              <div className="space-y-3">
                {visaTypes.map((visa, idx) => (
                  <motion.button
                    key={visa}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => handleVisaTypeClick(visa)}
                    className="w-full text-left p-5 rounded-lg border-2 border-slate-200 hover:border-trust hover:bg-trust/5 hover:shadow-md transition-all active:scale-[0.99] group"
                    data-testid={`visa-type-${visa.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-public font-semibold text-slate-900 group-hover:text-trust transition-colors">
                        {visa}
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-trust group-hover:translate-x-1 transition-all" />
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Notify Me Modal */}
      <AnimatePresence>
        {showNotifyModal && notifyRoute && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowNotifyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-trust/10 p-2 rounded-full">
                  <Bell className="w-6 h-6 text-trust" />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-lg text-slate-900">
                    Coming Soon!
                  </h3>
                  <p className="text-sm text-slate-600">
                    {notifyRoute.from} → {notifyRoute.to}
                  </p>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6">
                We're working on adding support for this migration route. Enter your email and we'll notify you as soon as it's available.
              </p>
              
              <form onSubmit={handleNotifySubmit}>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-trust/50 focus:border-trust"
                  data-testid="notify-email-input"
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowNotifyModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-md font-public font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-trust text-trust-foreground rounded-md font-public font-semibold hover:bg-trust/90 transition-colors"
                    data-testid="notify-submit-btn"
                  >
                    Notify Me
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CountrySelection;
